import { Head } from "@/components/Head";
import { Section, Eyebrow } from "@/components/Section";

export function LegalTermsPage() {
  return (
    <>
      <Head
        title="Terms of Service — ServiTrex"
        description="Draft terms of service for the ServiTrex platform and related services."
      />
      <Section narrow className="pt-14 sm:pt-16">
        <Eyebrow>Legal</Eyebrow>
        <h1 className="text-4xl sm:text-5xl">Terms of Service</h1>
        <p className="mt-4 text-sm text-muted">Last updated: July 16, 2026</p>

        <div className="prose-legal mt-10 space-y-8 text-sm leading-relaxed text-muted sm:text-base">
          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">1. Agreement</h2>
            <p className="mt-3">
              These Terms of Service (“Terms”) govern access to and use of ServiTrex,
              TrexField, related websites, applications, and services (collectively, the
              “Services”) provided by ServiTrex, a Cognautic company (“ServiTrex,” “we,”
              “us,” or “our”). By creating an account, executing an order form, or using
              the Services, you agree to these Terms on behalf of yourself and the
              organization you represent.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">2. The Services</h2>
            <p className="mt-3">
              ServiTrex provides multi-tenant field-service management software for
              home-service businesses, including office operations, field technician
              workflows, communications tooling, and optional modules such as accounting,
              inventory, workforce, marketing automation, and AI voice capabilities.
              Features available to you depend on your plan, add-ons, and configuration.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">3. Accounts & access</h2>
            <p className="mt-3">
              You are responsible for maintaining the confidentiality of account
              credentials, for the accuracy of information you provide, and for activity
              that occurs under your tenant. Administrators must assign roles
              appropriately and promptly revoke access when personnel leave your
              organization. You may not share login credentials across individuals.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">4. Customer data</h2>
            <p className="mt-3">
              You retain ownership of customer records, job data, media, and other content
              you submit to the Services (“Customer Data”). You grant ServiTrex a limited
              license to host, process, transmit, and display Customer Data solely to
              provide and improve the Services, prevent abuse, and comply with law. You
              represent that you have all rights and consents needed to submit Customer
              Data, including personal information of your customers and employees.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">5. Acceptable use</h2>
            <p className="mt-3">
              You will not misuse the Services. Prohibited conduct includes attempting to
              gain unauthorized access, interfering with platform integrity or other
              tenants, reverse engineering except where prohibited by law, using the
              Services for unlawful communications, sending spam, or uploading malicious
              code. Communications features may be limited to supported destinations and
              must comply with applicable telemarketing, privacy, and consumer-protection
              laws.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">6. AI voice & metered features</h2>
            <p className="mt-3">
              Certain capabilities — including AI voice agents, messaging, and AI credits —
              are metered. Usage beyond purchased bundles may incur overage charges at
              then-current rates. AI-generated outputs can be inaccurate or incomplete; you
              remain responsible for reviewing business-critical actions, customer
              communications, and operational decisions.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">7. Fees & billing</h2>
            <p className="mt-3">
              Fees are as stated on an order form, in-app plan selection, or other
              commercial agreement. Unless otherwise stated, subscriptions renew
              automatically and are non-refundable for partial periods. You authorize
              ServiTrex and its payment processors to charge applicable fees, taxes, and
              metered overages. Late amounts may result in suspension after notice.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">8. Third-party services</h2>
            <p className="mt-3">
              The Services may interoperate with carriers, payment processors, mapping
              providers, e-signature tools, and other third parties. Those services are
              governed by their own terms. ServiTrex is not responsible for third-party
              outages, policy changes, or acts outside our reasonable control.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">9. Confidentiality & security</h2>
            <p className="mt-3">
              Each party will protect the other’s confidential information with reasonable
              care. ServiTrex implements administrative, technical, and organizational
              safeguards designed to protect the Services, but no method of transmission or
              storage is perfectly secure. You are responsible for securing your own
              devices, networks, and user practices.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">10. Warranties & disclaimers</h2>
            <p className="mt-3">
              THE SERVICES ARE PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT
              PERMITTED BY LAW, SERVITREX DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS,
              IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT. We do not warrant that the Services will be
              uninterrupted or error-free.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">11. Limitation of liability</h2>
            <p className="mt-3">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SERVITREX WILL NOT BE LIABLE FOR
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY
              LOSS OF PROFITS, REVENUE, GOODWILL, OR DATA. OUR AGGREGATE LIABILITY UNDER
              THESE TERMS WILL NOT EXCEED THE AMOUNTS PAID BY YOU TO SERVITREX FOR THE
              SERVICES IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">12. Term & termination</h2>
            <p className="mt-3">
              These Terms remain in effect while you use the Services. Either party may
              terminate for material breach if the breach remains uncured after thirty (30)
              days’ notice, unless a shorter period is required to protect the platform or
              comply with law. Upon termination, your right to access the Services ends.
              Reasonable export of Customer Data may be available for a limited period
              subject to your account standing.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">13. Changes</h2>
            <p className="mt-3">
              We may update these Terms from time to time. Material changes will be
              communicated through the Services, email, or an updated posting date. Continued
              use after the effective date constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-tight text-fg">14. Contact</h2>
            <p className="mt-3">
              Questions about these Terms can be sent to legal@servitrex.com or to the
              business contact listed on your order form.
            </p>
          </section>
        </div>

        <p className="mt-12 rounded-2xl border border-border bg-surface-muted px-4 py-3 text-xs text-muted">
          Draft — pending counsel review. This page is a professional working draft for
          product launch preparation and is not final legal advice.
        </p>
      </Section>
    </>
  );
}
